/* eslint-disable react/display-name */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTracking } from 'react-tracking';
import type { ReactNode, ReactElement } from 'react';
import loadable from '@loadable/component';
import { Sentry } from '@/components/Sentry';
import { SUPPORT_EMAIL } from '@/constants';
import {
  Wrapper,
  ActionsPlaceHolder,
  DefaultOverlay,
  Container,
  Space,
} from './styled';
import Observer from '@researchgate/react-intersection-observer';
import { intersectorThresholds } from '@/constants/intersectorThresholds';
import { getViewMode } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import { ViewMode } from '@/pages/CompanyReport/enum';
import { LoadingSpinner, Grid, Text } from '@simplywallst/ui-core';

const ChartActions = loadable(
  () =>
    import('@/pages/CompanyReport/components/Overview/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActions,
    fallback: <ActionsPlaceHolder />,
  }
);

const RawDataWithRegoModal = loadable(
  () => import('./components/RawDataWithRegoModal'),
  {
    ssr: false,
  }
);

interface ChartWrapperProps {
  chart?: ReactElement;
  table?: ReactNode;
  rawData?: ReactNode;
  rawDataModal?: ReactNode;
  rawDataTitle?: ReactNode;
  id?: string;
  companyName?: string;
  helpTitle?: string;
  help?: ReactNode;
  feedbackLabel?: string;
  exportLightUrl?: string;
  exportDarkUrl?: string | null;
  keepChartRendered?: boolean;
  actionsMarginTop?: number;
  preRenderHeight?: number | string;
  preRenderHeightMdDown?: number | string;
  preRenderHeightSmDown?: number | string;
  moreActions?: ReactNode;
  defaultModeText?: string;
  hideDataButton?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  hideTableScrollbars?: boolean;
  children?: ReactNode;
}
export interface ChartWrapperRef {
  showChart: (show: boolean) => void;
}

/**
 * This component initially server side renders the table provided, then on intersection will render the chart provided
 *
 * Note: Don't need to wrap the chart with DeferRenderUntilIntersecting as its handled internally here
 */
const ChartWrapper = forwardRef<ChartWrapperRef, ChartWrapperProps>(
  (props, ref) => {
    const {
      chart,
      table,
      rawData,
      rawDataModal,
      rawDataTitle,
      id,
      companyName,
      helpTitle,
      help,
      feedbackLabel,
      exportLightUrl,
      exportDarkUrl,
      keepChartRendered = true,
      actionsMarginTop,
      preRenderHeight,
      preRenderHeightMdDown,
      preRenderHeightSmDown,
      moreActions,
      defaultModeText,
      hideDataButton,
      isLoading,
      loadingText,
      hideTableScrollbars,
      children,
    } = props;

    const [showTable, setShowTable] = useState<boolean>(true);
    const [showRawData, setShowRawData] = useState<boolean>(false);
    const [observed, setObserved] = useState<boolean>(false);
    const { trackEvent } = useTracking();
    const toggleData = () => {
      if (rawData) {
        setShowRawData((showRawData) => !showRawData);
      } else {
        trackEvent({
          id,
          action: 'click',
          modifier: 'toggleData',
          subject: showTable ? 'showChart' : 'showTable',
        });
        setShowTable((showTable) => !showTable);
      }
    };
    const viewMode = useSelector(getViewMode);

    useEffect(() => {
      if (viewMode === ViewMode.Print) {
        setObserved(true);
        setShowTable(false);
      }
    }, [viewMode]);

    useImperativeHandle(ref, () => ({
      showChart: (show) => {
        rawData ? setShowRawData(!show) : setShowTable(!show);
      },
    }));

    const customCopy = (
      <Text color="white" textAlign="center">
        <a href={SUPPORT_EMAIL} rel="nofollow">
          Send us an email
        </a>
        <span>{` to let us know what happened so we can fix the issue.`}</span>
      </Text>
    );

    if (isLoading) {
      return (
        <Sentry
          sentryCapture={{
            team: 'dingoes',
            extra: {
              chartWrapperId: id,
              companyName,
            },
          }}
          customCopy={customCopy}
          globalDisabled
        >
          <Container>
            <Wrapper
              data-cy-id={`${id}-wrapper`}
              showDefaultMode={defaultModeText !== undefined}
              hidden={false}
              height={preRenderHeight}
              heightMdDown={preRenderHeightMdDown}
              heightSmDown={preRenderHeightSmDown}
            >
              <Grid
                templateRows={preRenderHeight}
                templateRowsMd={preRenderHeightMdDown}
                templateRowsSm={preRenderHeightSmDown}
                placeItems={'center'}
              >
                <Grid placeItems={'center'}>
                  <LoadingSpinner light />
                  <Space />
                  {loadingText && (
                    <Text typography="xSmall">{loadingText}</Text>
                  )}
                </Grid>
              </Grid>
            </Wrapper>
          </Container>
          <ActionsPlaceHolder />
        </Sentry>
      );
    }

    return (
      <Sentry
        sentryCapture={{
          team: 'dingoes',
          extra: {
            chartWrapperId: id,
            companyName,
          },
        }}
        customCopy={customCopy}
        globalDisabled
      >
        {/* Chart */}
        {keepChartRendered ? (
          <Container>
            <Wrapper
              data-cy-id={`${id}-wrapper`}
              showDefaultMode={defaultModeText !== undefined}
              hidden={showTable}
              height={preRenderHeight}
              heightMdDown={preRenderHeightMdDown}
              heightSmDown={preRenderHeightSmDown}
            >
              {observed && chart}
            </Wrapper>
            {defaultModeText !== undefined && (
              <DefaultOverlay
                data-cy-id={`${id}-default`}
                justifyContent="center"
                alignItem="center"
              >
                {defaultModeText}
              </DefaultOverlay>
            )}
          </Container>
        ) : (
          !showTable && (
            <Container>
              <Wrapper
                data-cy-id={`${id}-wrapper`}
                height={preRenderHeight}
                heightMdDown={preRenderHeightMdDown}
                heightSmDown={preRenderHeightSmDown}
              >
                {chart}
              </Wrapper>
            </Container>
          )
        )}
        {/* Modal Data */}
        {showRawData && (
          <RawDataWithRegoModal
            rawData={rawData}
            setShowRawData={setShowRawData}
            showRegoModal={false}
          />
        )}
        <Observer
          disabled={observed}
          threshold={intersectorThresholds}
          rootMargin="600px 0px"
          onChange={({ isIntersecting }) => {
            if (isIntersecting) {
              setObserved(true);
              setShowTable(false);
            }
          }}
        >
          {/* Inline data table */}
          <Container>
            <Wrapper
              data-cy-id={`${id}-wrapper`}
              hidden={!showTable}
              scrollOnOverflow={showTable}
              height={preRenderHeight}
              heightMdDown={preRenderHeightMdDown}
              heightSmDown={preRenderHeightSmDown}
              hideTableScrollbars={hideTableScrollbars}
            >
              {showTable && table}
            </Wrapper>
          </Container>
        </Observer>
        {children}
        {observed && (moreActions || help || feedbackLabel || table) ? (
          <ChartActions
            id={id}
            companyName={companyName}
            helpTitle={helpTitle}
            help={help}
            feedbackLabel={feedbackLabel}
            exportLightUrl={exportLightUrl}
            exportDarkUrl={exportDarkUrl}
            toggleDataActive={showTable}
            onToggleData={toggleData}
            showDataButton={
              !hideDataButton && (table !== undefined || rawData !== undefined)
            }
            actionsMarginTop={actionsMarginTop}
            moreActions={moreActions}
            rawData={rawDataModal}
            rawDataTitle={rawDataTitle}
            shouldShowRegoModal={false}
          />
        ) : (
          <ActionsPlaceHolder />
        )}
      </Sentry>
    );
  }
);

export default ChartWrapper;
